import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getRevAllNodesWithStatus } from "../../../redux/procedureRevisionSlice";
import { generateAlphanumericSuffix } from "../../../util/AppUtils";

/**
 * useSubstepData Hook
 * 
 * Fetches revision nodes and calculates substep title, subheading, and sibling order
 * 
 * @param {Object} params
 * @param {string} params.partId - Part ID
 * @param {string} params.revisionId - Revision ID
 * @param {string} params.section_id_ref - Section ID (tree_node_id)
 * @param {string} params.step_id_ref - Step ID (tree_node_id)
 * @param {string} params.substep_id_ref - Substep ID (optional)
 * @param {string} params.partnumber - Part number for subheading
 * @param {string} params.revision - Revision for subheading
 * @param {string} params.mode - "add" or "edit"
 * 
 * @returns {Object} { loading, allNodesData, substepTitle, sectionTitle, stepTitle, sectionIdRef, stepIdRef, subheading, siblingOrder, currentSubstepData }
 */
const useSubstepData = ({ partId, revisionId, section_id_ref, step_id_ref, substep_id_ref, partnumber, revision, mode }) => {
  const dispatch = useDispatch();
  const [allNodesData, setAllNodesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [substepTitle, setSubstepTitle] = useState("Substep");
  const [sectionTitle, setSectionTitle] = useState("Section");
  const [stepTitle, setStepTitle] = useState("Step");
  const [sectionIdRef, setSectionIdRef] = useState(null);
  const [stepIdRef, setStepIdRef] = useState(null);
  const [subheading, setSubheading] = useState("");
  const [siblingOrder, setSiblingOrder] = useState(null);
  const [currentSubstepData, setCurrentSubstepData] = useState(null);
  const { currentStatus, item_code_type } = useSelector((state) => state.procedureRevision);

  useEffect(() => {
    const fetchNodes = async () => {
      try {
        setLoading(true);
        const result = await dispatch(
          getRevAllNodesWithStatus({
            partId: partId,
            revisionId: revisionId,
          })
        );

        if (result.payload) {
          setAllNodesData(result.payload.data);

          // Calculate indices from data (similar to ViewStep component)
          const compareSiblingOrders = (a, b) => {
            const intA = parseInt(a, 10);
            const intB = parseInt(b, 10);
            return intA - intB;
          };

          const sortedSections = [...result.payload.data].sort((a, b) => {
            const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
            const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
            const orderCompare = compareSiblingOrders(orderA, orderB);
            if (orderCompare !== 0) return orderCompare;
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return dateB - dateA;
          });

          // Find section index
          const sectionIndex = sortedSections.findIndex(
            (section) => section.tree_node_id === section_id_ref
          );
          const currentSection = sortedSections[sectionIndex];
          setSectionIdRef(currentSection.section_id_ref);

          // Find step index and step ID ref
          let stepIndex = 0;
          let currentStep = null;
          if (currentSection?.steps && Array.isArray(currentSection.steps)) {
            stepIndex = currentSection.steps.findIndex(
              (step) => step.tree_node_id === step_id_ref
            );
            currentStep = currentSection.steps[stepIndex];
            setStepIdRef(currentStep.step_id_ref);
          }

          // Calculate global step number
          const globalStepStart = sortedSections.slice(0, sectionIndex).reduce(
            (total, section) => total + (section.steps?.length || 0), 0
          );
          const globalStepNumber = globalStepStart + stepIndex + 1;

          // Calculate sibling order
          let calculatedSiblingOrder = null;
          if (substep_id_ref) {
            // Find the substep and get its sibling_order + 1
            if (currentStep?.substeps && Array.isArray(currentStep.substeps)) {
              const targetSubstep = currentStep.substeps.find(
                (substep) => substep.tree_node_id === substep_id_ref
              );
              if (targetSubstep) {
                const substepSiblingOrder = targetSubstep.sibling_order || targetSubstep.procedure_tree?.sibling_order || 0;
                calculatedSiblingOrder = parseInt(substepSiblingOrder, 10) + 1;
                setCurrentSubstepData(targetSubstep); // Store the substep data for edit mode
              }
            }
          } else {
            // Count total substeps in current step + 1
            if (currentStep?.substeps && Array.isArray(currentStep.substeps)) {
              calculatedSiblingOrder = currentStep.substeps.length + 1;
            } else {
              calculatedSiblingOrder = 1;
            }
          }
          setSiblingOrder(calculatedSiblingOrder);

          // Calculate substep title with alphanumeric pattern
          let substepTitle = "";
          if (substep_id_ref && mode === "edit") {
            // Find the substep with substep_id_ref
            if (currentStep?.substeps && Array.isArray(currentStep.substeps)) {
              const substepIndex = currentStep.substeps.findIndex(
                (substep) => substep.tree_node_id === substep_id_ref
              );
              if (substepIndex !== -1) {
                const stepNumber = globalStepNumber;
                const suffix = generateAlphanumericSuffix(substepIndex);
                substepTitle = `Substep ${stepNumber}${suffix}`;
              }
            }
          } else if (mode === "add") {
            // Calculate total substeps in current step for new substep
            let totalSubsteps = 0;
            if (currentStep?.substeps && Array.isArray(currentStep.substeps)) {
              totalSubsteps = currentStep.substeps.length;
            }
            const stepNumber = globalStepNumber;
            const suffix = generateAlphanumericSuffix(totalSubsteps);
            substepTitle = `Substep ${stepNumber}${suffix}`;
          }
          setSubstepTitle(substepTitle);

          // Build subheading using global step number
          const sectionNumber = sectionIndex + 1;
          setSectionTitle(`Section ${sectionNumber}`);
          setStepTitle(`Step ${globalStepNumber}`);
          const subheadingText = `${partnumber} > Rev ${revision} > Section ${sectionNumber} > Step ${globalStepNumber} > ${substepTitle}`;
          setSubheading(subheadingText);
        }
      } catch (error) {
        console.error("Error fetching revision nodes:", error);
      } finally {
        setLoading(false);
      }
    };

    if (partId && revisionId && section_id_ref && step_id_ref) {
      fetchNodes();
    }
  }, [partId, revisionId, section_id_ref, step_id_ref, substep_id_ref, dispatch, partnumber, revision, mode]);

  return {
    loading,
    allNodesData,
    substepTitle,
    sectionTitle,
    stepTitle,
    sectionIdRef,
    stepIdRef,
    subheading,
    siblingOrder: siblingOrder?.toString(),
    currentSubstepData,
    currentStatus,
    item_code_type,
  };
};

export default useSubstepData;
